import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'clsx';
import styles from './styles.module.scss';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import ChipList from '@components-clean/core/chip-list';
import { getHighlightClass } from '@components-clean/core/grid-fe/custom-cells';
import { InfoField } from '@components-clean/core/info-field';
import Tooltip from '@components-clean/core/tooltip';
import AddRiskDialog, {
    RiskFormInputs,
} from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/add-risk-dialog';
import { RiskLogChipsValues } from '@pages-clean/risk-assessment/risk-log/constants';
import CompleteRiskReviewDialog from '@pages-clean/risk-assessment/risk-log/dialogs/complete-risk-review-dialog';
import { RiskLogContext } from '@pages-clean/risk-assessment/risk-log/interfaces/context';
import AttributesArea from '@pages-clean/risk-assessment/risk-log/sections/childArea/attributes/attributes';
import CmControlsArea from '@pages-clean/risk-assessment/risk-log/sections/childArea/cm-controls/cm-controls.grid';
import ControlsArea from '@pages-clean/risk-assessment/risk-log/sections/childArea/controls';
import CtQsArea from '@pages-clean/risk-assessment/risk-log/sections/childArea/ctqs';
import EvaluationArea from '@pages-clean/risk-assessment/risk-log/sections/childArea/evaluation/evaluation';
import MonitoringArea from '@pages-clean/risk-assessment/risk-log/sections/childArea/monitoring/monitoring';
import RiskInformation from '@pages-clean/risk-assessment/risk-log/sections/childArea/risk-information/risk-information';
import { CountryInputType, RiskStatusType, TimeFrequencyType } from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useBoolean } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromLocal, stringDateWithoutTimeFromUtc, getIsActiveField } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { mapRiskReviewStatusIcon } from '@utils/risk-helper';
import { upsertRisk, upsertRiskVariables } from '@queries/generated/risk-assessment.generated';
import { CTQ_WIDGET_VALUES, GET_COMPLETE_RISKS_RISK_LOG, UPSERT_RISK } from '@queries/risk-assessment';

const RiskLogChildAreas = () => {
    const user = useAuthorization();
    const canEdit = user?.canWrite?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });
    const [chip, setChip] = useState<RiskLogChipsValues>(RiskLogChipsValues.Attributes);
    const { t }: { t: (key: string) => string } = useTranslation('ram', {
        keyPrefix: 'riskLog',
    });
    const { t: tRisk }: { t: (key: string) => string } = useTranslation('ram', {
        keyPrefix: 'risks',
    });
    const { t: tCommon } = useTranslation('common');
    const { current } = useContext(RiskLogContext);
    const showNewDatasetInfoIcon = useBoolean(false);

    useEffect(() => {
        if (
            current.risksHaveCmControlsLinked &&
            current.latestDataset &&
            current.riskReviewStartDate &&
            current.latestDataset > current.riskReviewStartDate
        )
            showNewDatasetInfoIcon.setTrue();
        else showNewDatasetInfoIcon.setFalse();
    }, [current.latestDataset, current.riskReviewStartDate, current.risksHaveCmControlsLinked]);

    const { study } = current;
    const StudyHasCM2 = study && getIsActiveField('CM2', study.modules);
    const HasCMControlsPermission = user?.can?.({
        module: Constants.Modules.RAM,
        permission: Permissions.Enabled,
        element: Constants.PageElements.CMControls,
    });
    const HasRiskReportsPermission = user?.can?.({
        module: Constants.Modules.RAM,
        permission: Permissions.Enabled,
        element: Constants.PageElements.RiskReports,
    });

    const [upsertRiskMutation] = useMutation<upsertRisk, upsertRiskVariables>(UPSERT_RISK);
    const [isLargeView, setIsLargeView] = React.useState(true);
    const showReviewCompleteDialog = useBoolean(false);
    const showEditRiskDialog = useBoolean(false);
    const risk = current.selectionState.selectedItem;
    const selectedRiskId = risk?.id ?? 'no-risk';
    const recordId = risk?.recordId;
    const containerRef = useRef(null);
    const riskLogChildAreasCardEl = document.getElementsByClassName('riskLogChildAreasCard');
    const reviewStatusIcon = mapRiskReviewStatusIcon(risk?.reviewStatus);
    const reviewStatus = risk?.reviewStatus as string;

    useEffect(() => {
        const listEl =
            riskLogChildAreasCardEl.length > 0
                ? riskLogChildAreasCardEl[0].getElementsByClassName('card-header')
                : null;
        if (listEl && current.selectionState.selectedItemsCount === 1) {
            const riskLogChildAreasCardHeaderEl = listEl[0];
            const observer = new ResizeObserver((entries) => {
                const body = entries[0];
                if (body.contentRect.width <= 1030) setIsLargeView(false);
                else setIsLargeView(true);
            });
            observer.observe(riskLogChildAreasCardHeaderEl);

            return () => observer.disconnect();
        }
    }, [current.selectionState.selectedItemsCount]);

    if (current.selectionState.selectedItemsCount === 0)
        return (
            <div className="k-h-full">
                <InfoField type="Info" message={t('childAreas.info.noParentRecordSelected')} layout="center" />
            </div>
        );

    if (current.selectionState.selectedItemsCount > 1)
        return (
            <div className="k-h-full">
                <InfoField type="Info" message={t('childAreas.info.multipleParentRecords')} layout="center" />
            </div>
        );

    const getIconColor = (color: string) => {
        switch (color) {
            case 'primary':
                return styles.primary;
            case 'secondary':
                return styles.secondary;
            case 'green':
                return styles.green;
            case 'grey':
                return styles.grey;
            default:
                return styles.grey;
        }
    };

    const onEditRiskClose = () => {
        showEditRiskDialog.setFalse();
    };

    const onSubmitEditRisk = async (editRiskData: RiskFormInputs) => {
        upsertRiskMutation({
            variables: {
                risk: {
                    id: risk?.id,
                    studyId: study?.id,
                    libraryReferenceId: risk?.libraryReferenceId,
                    ownerId: editRiskData.owner?.id,
                    controlStrategyId: editRiskData.controlStrategy?.id,
                    categoryId: editRiskData.category?.id,
                    responsibleFunctionId: editRiskData.responsibleFunction?.id,
                    statement: editRiskData.statement,
                    // The user does not set this in the dialog (they set it in the Complete Review dialog instead)
                    // so the date always comes direct from the DB - treat it as UTC
                    dateLastReviewed: risk?.dateLastReviewed
                        ? stringDateWithoutTimeFromUtc(risk.dateLastReviewed)
                        : null,
                    dateIdentified: editRiskData.dateIdentified
                        ? stringDateWithoutTimeFromLocal(editRiskData.dateIdentified)
                        : null,
                    status: RiskStatusType[editRiskData.status?.id as keyof typeof RiskStatusType],
                    recordId: risk?.recordId,
                    reviewRequired: editRiskData.reviewRequired,
                    reviewFrequencyPeriod: editRiskData.reviewRequired ? editRiskData.reviewFrequencyPeriod : null,
                    reviewFrequencyUnits: editRiskData.reviewRequired
                        ? TimeFrequencyType[editRiskData.reviewFrequencyUnit?.id as keyof typeof TimeFrequencyType]
                        : null,
                    controls: risk?.controlIds,
                    active: risk?.active,
                    internalOnly: editRiskData.internal,
                    vendorIds: editRiskData.vendors?.map((v) => v.id) ?? [],
                    countries: editRiskData.countries?.map((c) => c as CountryInputType) ?? [],
                    qualityIssueId: editRiskData.qualityIssue?.id,
                    qualityIssueReference: editRiskData.qualityIssueReference,
                },
            },
            refetchQueries: [
                {
                    query: CTQ_WIDGET_VALUES,
                    variables: { studyId: study?.id },
                },
                { query: GET_COMPLETE_RISKS_RISK_LOG, variables: { studyId: study?.id } },
            ],
        })
            .then(async (response) => {
                if (!response?.data?.upsertRisk) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                showEditRiskDialog.setFalse();
                ShowSuccess(tCommon('actionsSuccessfulToast'));
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const cmControlsChip =
        HasCMControlsPermission && StudyHasCM2
            ? {
                  text: t('chips.cmControls'),
                  value: RiskLogChipsValues.CmControls,
                  endAdornment:
                      showNewDatasetInfoIcon.value && HasRiskReportsPermission ? (
                          <FontAwesomeIcon
                              icon={['fadl', 'circle']}
                              className={cn(
                                  'k-ml-1.5',
                                  chip !== RiskLogChipsValues.CmControls ? styles.secondary : styles.white,
                              )}
                              size="2xs"
                          />
                      ) : undefined,
              }
            : undefined;

    return (
        <div style={{ display: 'flex', height: '100%' }}>
            <RiskInformation />
            <Card
                className="riskLogChildAreasCard"
                data-testid="risk-log-child-area"
                title={
                    <div className={styles.titleContainer} ref={containerRef}>
                        {recordId && (
                            <>
                                <div className="k-align-self-center">{recordId}</div>
                                <div className={cn(styles.spacer, 'k-align-self-center')}>&nbsp;</div>
                                {current.selectionState.selectedItemsCount > 1 || !risk?.score?.score ? (
                                    <span className={cn(styles.evalScore, styles.noScore)}>-</span>
                                ) : (
                                    <span
                                        className={cn(
                                            getHighlightClass(risk?.score?.colour ?? 'None'),
                                            styles.evalScore,
                                            'k-align-self-center',
                                        )}
                                        style={{ textAlign: !risk?.score?.score ? 'center' : 'right' }}
                                    >
                                        {isLargeView && `${t('shared.score')}: `} {risk?.score?.score}
                                    </span>
                                )}
                                <div className={styles.spacer}>&nbsp;</div>
                            </>
                        )}
                        <ChipList
                            data-testid="risks-reviews-chip-list"
                            data={[
                                {
                                    text: t('chips.attributes'),
                                    value: RiskLogChipsValues.Attributes,
                                },
                                {
                                    text: t('chips.evaluation'),
                                    value: RiskLogChipsValues.Evaluation,
                                },
                                {
                                    text: t('chips.ctqs'),
                                    value: RiskLogChipsValues.CtQs,
                                },
                                {
                                    text: t('chips.controls'),
                                    value: RiskLogChipsValues.Controls,
                                },
                                {
                                    text: t('chips.monitoring'),
                                    value: RiskLogChipsValues.Monitoring,
                                },
                                ...(cmControlsChip ? [cmControlsChip] : []),
                            ]}
                            value={chip}
                            onChange={(event) => {
                                const selectedChip = event.value as RiskLogChipsValues;
                                if (selectedChip) setChip(selectedChip);
                            }}
                        />
                    </div>
                }
                removeHeaderActionStyle
                headerActions={
                    <div className={styles.titleContainer}>
                        {chip === RiskLogChipsValues.Attributes && (
                            <>
                                <Tooltip parentTitle position={isLargeView ? 'auto' : 'left'}>
                                    <Button
                                        data-testid="ram-edit-attributes"
                                        onClick={() => {
                                            showEditRiskDialog.setTrue();
                                        }}
                                        primary
                                        endIcon={
                                            isLargeView ? undefined : (
                                                <FontAwesomeIcon
                                                    icon={['far', 'pen-to-square']}
                                                    className={isLargeView ? 'k-ml-1.5' : ''}
                                                />
                                            )
                                        }
                                        className={isLargeView ? '' : styles.iconButton}
                                        title={isLargeView ? undefined : t('buttons.editAttributes')}
                                        disabled={!canEdit}
                                    >
                                        {isLargeView && t('buttons.editAttributes')}
                                    </Button>
                                </Tooltip>
                                <div className={styles.spacer}>&nbsp;</div>
                            </>
                        )}
                        <span>
                            {reviewStatus !== t('shared.n/a')
                                ? reviewStatus
                                : tRisk('riskReviewStatus.REVIEW_NOT_REQUIRED')}
                        </span>
                        {reviewStatusIcon && reviewStatusIcon.color && (
                            <FontAwesomeIcon
                                icon={reviewStatusIcon.icon}
                                className={cn(getIconColor(reviewStatusIcon.color), 'k-align-self-center')}
                                fontSize="16px"
                            />
                        )}
                        <div className={styles.spacer}>&nbsp;</div>
                        <Tooltip parentTitle position={isLargeView ? 'auto' : 'left'}>
                            <Button
                                data-testid="ram-review-complete"
                                onClick={() => {
                                    showReviewCompleteDialog.setTrue();
                                }}
                                primary
                                alternativeStyle
                                endIcon={
                                    isLargeView ? undefined : (
                                        <FontAwesomeIcon
                                            icon={['far', 'calendar-check']}
                                            className={isLargeView ? 'k-ml-1.5' : ''}
                                        />
                                    )
                                }
                                className={isLargeView ? '' : styles.iconButton}
                                title={isLargeView ? undefined : t('buttons.completeReview')}
                                disabled={!canEdit}
                            >
                                {isLargeView && t('buttons.completeReview')}
                            </Button>
                        </Tooltip>
                    </div>
                }
            >
                <>
                    {chip === RiskLogChipsValues.Attributes && <AttributesArea key={`attributes-${selectedRiskId}`} />}
                    {chip === RiskLogChipsValues.Evaluation && <EvaluationArea key={`evaluation-${selectedRiskId}`} />}
                    {chip === RiskLogChipsValues.CtQs && <CtQsArea key={`ctqs-${selectedRiskId}`} />}
                    {chip === RiskLogChipsValues.Controls && <ControlsArea key={`controls-${selectedRiskId}`} />}
                    {chip === RiskLogChipsValues.Monitoring && <MonitoringArea key={`monitoring-${selectedRiskId}`} />}
                    {chip === RiskLogChipsValues.CmControls && <CmControlsArea key={`monitoring-${selectedRiskId}`} />}
                    {showReviewCompleteDialog.value && risk && (
                        <CompleteRiskReviewDialog
                            onClose={() => showReviewCompleteDialog.setFalse()}
                            onSubmit={() => {
                                showReviewCompleteDialog.setFalse();
                            }}
                            risk={risk}
                            showNewDatasetWarning={
                                current.latestDataset &&
                                current.riskReviewStartDate &&
                                current.latestDataset > current.riskReviewStartDate &&
                                HasCMControlsPermission &&
                                HasRiskReportsPermission &&
                                (risk.associatedControlsHubPanelIds?.length ?? 0) > 0
                            }
                        />
                    )}
                    {showEditRiskDialog.value && risk && (
                        <AddRiskDialog
                            onClose={onEditRiskClose}
                            onSubmit={onSubmitEditRisk}
                            studyRef={study?.id}
                            risk={risk}
                        />
                    )}
                </>
            </Card>
        </div>
    );
};

export default RiskLogChildAreas;
